import { createFileRoute, Link } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { useMemo, useState } from "react"
import { api } from "../../convex/_generated/api"
import type { Id } from "../../convex/_generated/dataModel"
import { AdminJobApplicants } from "@/components/admin/admin-job-applicants"
import {
  AdminFilterPills,
  AdminListPage,
  AdminRow,
} from "@/components/admin/admin-list-page"
import { Badge } from "@/components/ui/badge"
import { StatusPill } from "@/components/status-pill"
import { Button } from "@/components/ui/button"
import { ShareJobButton } from "@/features/jobs"
import { useConfirmAction } from "@/hooks/use-confirm-action"

export const Route = createFileRoute("/admin/jobs")({
  component: AdminJobsPage,
})

type CompanyFilter = "active" | "rejected" | "all"
type JobStatusFilter = "all" | "open" | "closed"
type VisibilityFilter = "all" | "public" | "hidden"
type ApplicantsFilter = "all" | "with" | "none"

const COMPANY_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "rejected", label: "Rejected companies" },
  { value: "all", label: "All companies" },
] as const

const JOB_STATUS_OPTIONS = [
  { value: "all", label: "All jobs" },
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
] as const

const VISIBILITY_OPTIONS = [
  { value: "all", label: "Any listing" },
  { value: "public", label: "Public" },
  { value: "hidden", label: "Hidden" },
] as const

const APPLICANTS_OPTIONS = [
  { value: "all", label: "Any applicants" },
  { value: "with", label: "With applicants" },
  { value: "none", label: "Without applicants" },
] as const

function applicantLabel(count: number) {
  if (count === 0) return "No applicants"
  if (count === 1) return "1 applicant"
  return `${count} applicants`
}

export function AdminJobsPage() {
  const [companyFilter, setCompanyFilter] = useState<CompanyFilter>("active")
  const [jobStatus, setJobStatus] = useState<JobStatusFilter>("all")
  const [visibility, setVisibility] = useState<VisibilityFilter>("all")
  const [applicants, setApplicants] = useState<ApplicantsFilter>("all")
  const [search, setSearch] = useState("")
  const [expandedJobId, setExpandedJobId] = useState<Id<"jobs"> | null>(null)

  const jobsArgs = useMemo(() => {
    const needle = search.trim()
    return {
      ...(companyFilter === "active" ? {} : { companyStatus: companyFilter }),
      ...(jobStatus === "all" ? {} : { jobStatus }),
      ...(visibility === "all" ? {} : { visibility }),
      ...(applicants === "all" ? {} : { applicants }),
      ...(needle ? { q: needle } : {}),
    }
  }, [applicants, companyFilter, jobStatus, search, visibility])

  const jobs = useQuery(api.admin.listJobs, jobsArgs)
  const forceClose = useMutation(api.admin.forceCloseJob)
  const { confirmAction } = useConfirmAction()

  const closeJob = (jobId: Id<"jobs">, title: string) =>
    confirmAction(
      `Force-close "${title}"? It will leave public browse.`,
      () => forceClose({ jobId }),
      { success: "Job closed" },
    )

  const toggleApplicants = (jobId: Id<"jobs">) =>
    setExpandedJobId((current) => (current === jobId ? null : jobId))

  return (
    <AdminListPage
      title="Jobs"
      description="See who applied, filter by listing state, and take down jobs when needed."
      search={{
        value: search,
        onChange: setSearch,
        placeholder: "Search title, company, or location",
      }}
      toolbar={
        <div className="space-y-2">
          <AdminFilterPills
            label="Company"
            value={companyFilter}
            onChange={setCompanyFilter}
            options={COMPANY_OPTIONS}
          />
          <AdminFilterPills
            label="Job"
            value={jobStatus}
            onChange={setJobStatus}
            options={JOB_STATUS_OPTIONS}
          />
          <AdminFilterPills
            label="Listing"
            value={visibility}
            onChange={setVisibility}
            options={VISIBILITY_OPTIONS}
          />
          <AdminFilterPills
            label="Applicants"
            value={applicants}
            onChange={setApplicants}
            options={APPLICANTS_OPTIONS}
          />
        </div>
      }
      data={jobs}
      emptyCopy="No jobs match this filter."
      renderRow={(job) => {
        const expanded = expandedJobId === job._id
        return (
          <AdminRow
            key={job._id}
            align={expanded ? "start" : "center"}
            title={
              <Link
                to="/jobs/$jobId"
                params={{ jobId: job._id }}
                target="_blank"
                rel="noopener noreferrer"
                className="underline-offset-4 hover:underline"
              >
                {job.title}
              </Link>
            }
            badges={
              <>
                <StatusPill domain="job" status={job.status} as="badge" />
                {job.publiclyVisible ? (
                  <Badge variant="outline">Public</Badge>
                ) : (
                  <Badge variant="destructive">Hidden</Badge>
                )}
                {job.applicantCount > 0 ? (
                  <Button
                    size="sm"
                    variant={expanded ? "default" : "secondary"}
                    aria-expanded={expanded}
                    onClick={() => toggleApplicants(job._id)}
                  >
                    {applicantLabel(job.applicantCount)}
                  </Button>
                ) : (
                  <Badge variant="outline">{applicantLabel(0)}</Badge>
                )}
              </>
            }
            meta={
              <>
                {job.companyName ?? "Unknown company"}
                {job.companyStatus ? ` · ${job.companyStatus}` : ""} ·{" "}
                {job.location}
              </>
            }
            actions={
              <div className="flex flex-wrap items-center gap-2">
                {job.applicantCount > 0 ? (
                  <Button
                    size="sm"
                    variant="outline"
                    aria-expanded={expanded}
                    onClick={() => toggleApplicants(job._id)}
                  >
                    {expanded ? "Hide applicants" : "View applicants"}
                  </Button>
                ) : null}
                <Button size="sm" variant="outline" asChild>
                  <Link
                    to="/jobs/$jobId"
                    params={{ jobId: job._id }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View listing
                  </Link>
                </Button>
                {job.publiclyVisible ? (
                  <ShareJobButton
                    jobId={job._id}
                    title={job.title}
                    companyName={job.companyName}
                  />
                ) : null}
                {job.status === "open" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void closeJob(job._id, job.title)}
                  >
                    Force close
                  </Button>
                )}
              </div>
            }
          >
            {expanded ? <AdminJobApplicants jobId={job._id} /> : null}
          </AdminRow>
        )
      }}
    />
  )
}
